import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  KeyRound,
  GraduationCap,
  Thermometer,
  Copy,
  Check,
  Loader2,
  ShieldCheck,
  Users,
  Building2,
  Layers,
  Ban,
  Plus,
  Link2,
  AlertTriangle,
  Upload,
  Lock,
  Rocket,
  Send,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, Button } from '../components';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import api from '../services/api';
import type { EmployerLocation, EmployerDepartment } from '../components/EmployerOrgStructuur';
import {
  getEmployerPortaal,
  maakToegangscode,
  deactiveerToegangscode,
  updateAcademySettings,
  uploadCaoBudgetten,
  getWkrOverzicht,
  getHittekaart,
  importUitnodigingen,
  activeerUitnodigingen,
  type EmployerPortaalOverzicht,
  type Toegangscode,
  type AcademySettings,
  type CaoImportResultaat,
  type UitnodigingenImportResultaat,
  type WkrOverzicht,
  type WkrLabel,
  type HittekaartGroep,
  type ThermometerPijlerKey,
} from '../services/medewerkerApi';

type PortaalTab = 'toegang' | 'academy' | 'thermometer' | 'activeren';

const TABS: Array<{ key: PortaalTab; label: string; description: string; icon: React.ComponentType<{ className?: string }> }> = [
  { key: 'toegang', label: 'Toegang', description: 'Toegangscodes en aantallen geregistreerde medewerkers', icon: KeyRound },
  { key: 'academy', label: 'Academy', description: 'L&D-budget, WKR-overzicht en frequentie Bedrijfsthermometer', icon: GraduationCap },
  { key: 'activeren', label: 'Activeren', description: 'Personeelslijst importeren en proactief per SMS + e-mail uitnodigen', icon: Rocket },
  { key: 'thermometer', label: 'Thermometer', description: 'Hittekaart Bedrijfsthermometer per vestiging en afdeling', icon: Thermometer },
];

export const PORTAAL_QUERY_KEY = ['employer-medewerkersportaal'];

const WKR_LABELS: Record<WkrLabel, string> = {
  gerichte_vrijstelling: 'Gerichte vrijstelling (belastingvrij, buiten WKR)',
  wkr_vrije_ruimte: 'Drukt op WKR-vrije ruimte',
};

const euro = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });
const fmtEuro = (n: number | null | undefined) => euro.format(Number(n ?? 0));
const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('nl-NL') : '—');

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
const labelClass = 'block text-xs font-medium text-gray-700 mb-1';

const apiMessage = (err: any, fallback: string): string => err?.response?.data?.message || fallback;

/** Klein kopieerknopje: klembord + 2s vinkje. */
const CopyButton: React.FC<{ value: string; label: string; className?: string }> = ({ value, label, className }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Kopiëren mislukt — selecteer en kopieer handmatig.');
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={label}
      title={label}
      className={`inline-flex items-center justify-center rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 ${className || ''}`}
    >
      {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
    </button>
  );
};

const Spinner: React.FC = () => (
  <div className="flex items-center justify-center py-24">
    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
  </div>
);

/** Hook: vestigingen + afdelingen (zelfde query-keys als EmployerOrgStructuur). */
const useOrgStructuur = () => {
  const { data: locations = [] } = useQuery({
    queryKey: ['employer-locations'],
    queryFn: async () => (await api.get('/employer/locations')).data?.data as EmployerLocation[],
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['employer-departments'],
    queryFn: async () => (await api.get('/employer/departments')).data?.data as EmployerDepartment[],
  });
  return { locations, departments };
};

// ─── Tab 1: Toegang ───────────────────────────────────────────────────────────

const LEGE_CODE_FORM = { omschrijving: '', location_id: '', department_id: '', max_gebruik: '', verloopt_op: '' };

const ToegangTab: React.FC<{ data: EmployerPortaalOverzicht }> = ({ data }) => {
  const { t } = useTranslation('employer');
  const queryClient = useQueryClient();
  const { locations, departments } = useOrgStructuur();
  const [form, setForm] = useState({ ...LEGE_CODE_FORM });
  const [nieuweCode, setNieuweCode] = useState<Toegangscode | null>(null);

  // Afdelingen die bij de gekozen vestiging horen (of universeel zijn)
  const afdelingOpties = useMemo(
    () => departments.filter((d) => !form.location_id || !d.location_id || d.location_id === form.location_id),
    [departments, form.location_id]
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: PORTAAL_QUERY_KEY });

  const maakMutation = useMutation({
    mutationFn: () =>
      maakToegangscode({
        omschrijving: form.omschrijving.trim() || null,
        location_id: form.location_id || null,
        department_id: form.department_id || null,
        max_gebruik: form.max_gebruik ? Number(form.max_gebruik) : null,
        verloopt_op: form.verloopt_op || null,
      }),
    onSuccess: (code) => {
      toast.success(t('portaalBeheer.codeAangemaakt', 'Toegangscode aangemaakt'));
      setNieuweCode(code);
      setForm({ ...LEGE_CODE_FORM });
      invalidate();
    },
    onError: (err: any) => toast.error(apiMessage(err, t('portaalBeheer.codeFout', 'Toegangscode aanmaken mislukt'))),
  });

  const deactiveerMutation = useMutation({
    mutationFn: (id: string) => deactiveerToegangscode(id),
    onSuccess: () => {
      toast.success(t('portaalBeheer.codeGedeactiveerd', 'Toegangscode gedeactiveerd'));
      invalidate();
    },
    onError: (err: any) => toast.error(apiMessage(err, t('portaalBeheer.deactiverenFout', 'Deactiveren mislukt'))),
  });

  const registratieLink = (code: string) => `${window.location.origin}/medewerker/registreren?code=${encodeURIComponent(code)}`;

  const { statistiek, toegangscodes } = data;

  return (
    <div className="space-y-6">
      {/* Statistiek */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <Users className="w-5 h-5 text-blue-600" />
          {t('portaalBeheer.statTitel', 'Geregistreerde medewerkers')}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          {t('portaalBeheer.statUitleg', 'Alleen aantallen. Wie zich registreert, is voor u nooit zichtbaar.')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="text-3xl font-bold text-blue-700">{statistiek.totaal}</div>
            <div className="text-xs text-blue-900 mt-1">{t('portaalBeheer.statTotaal', 'medewerkers in totaal')}</div>
          </div>
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase mb-2">
              <Building2 className="w-3.5 h-3.5" />
              {t('portaalBeheer.statPerVestiging', 'Per vestiging')}
            </div>
            {statistiek.per_vestiging.length === 0 ? (
              <p className="text-sm text-gray-400">—</p>
            ) : (
              <ul className="space-y-1">
                {statistiek.per_vestiging.map((v) => (
                  <li key={v.location_id ?? 'geen'} className="flex justify-between text-sm">
                    <span className="text-gray-800 truncate mr-2">{v.naam}</span>
                    <span className="font-medium tabular-nums">{v.aantal}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase mb-2">
              <Layers className="w-3.5 h-3.5" />
              {t('portaalBeheer.statPerAfdeling', 'Per afdeling')}
            </div>
            {statistiek.per_afdeling.length === 0 ? (
              <p className="text-sm text-gray-400">—</p>
            ) : (
              <ul className="space-y-1">
                {statistiek.per_afdeling.map((a) => (
                  <li key={a.department_id ?? 'geen'} className="flex justify-between text-sm">
                    <span className="text-gray-800 truncate mr-2">{a.naam}</span>
                    <span className="font-medium tabular-nums">{a.aantal}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Card>

      {/* Nieuwe toegangscode */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <Plus className="w-5 h-5 text-blue-600" />
          {t('portaalBeheer.nieuweCodeTitel', 'Nieuwe toegangscode')}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          {t(
            'portaalBeheer.nieuweCodeUitleg',
            'Deel de code of link met medewerkers. Zij registreren zich anoniem; u ziet nooit wie zich aanmeldt — alleen aantallen.'
          )}
        </p>

        {nieuweCode && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-start gap-2 text-sm text-emerald-900 mb-3">
              <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <span>
                {t('portaalBeheer.codeKlaar', 'Uw nieuwe toegangscode is klaar. Deel de code of de registratielink met uw medewerkers.')}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center gap-2 rounded-md bg-white border border-emerald-200 px-3 py-2">
                <span className="text-xs text-gray-500">{t('portaalBeheer.code', 'Code')}</span>
                <code className="font-mono text-base font-semibold tracking-wider text-gray-900">{nieuweCode.code}</code>
                <CopyButton value={nieuweCode.code} label={t('portaalBeheer.kopieerCode', 'Code kopiëren')} />
              </div>
              <div className="flex items-center gap-2 rounded-md bg-white border border-emerald-200 px-3 py-2 min-w-0 flex-1">
                <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <code className="font-mono text-xs text-gray-800 truncate">{registratieLink(nieuweCode.code)}</code>
                <CopyButton value={registratieLink(nieuweCode.code)} label={t('portaalBeheer.kopieerLink', 'Link kopiëren')} />
              </div>
            </div>
          </div>
        )}

        <form
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            maakMutation.mutate();
          }}
        >
          <div className="sm:col-span-2 lg:col-span-3">
            <label className={labelClass}>{t('portaalBeheer.omschrijving', 'Omschrijving')}</label>
            <input
              type="text"
              className={inputClass}
              placeholder={t('portaalBeheer.omschrijvingPh', 'bijv. Alle medewerkers 2026 / Productie Eindhoven')}
              value={form.omschrijving}
              onChange={(e) => setForm((p) => ({ ...p, omschrijving: e.target.value }))}
            />
          </div>
          <div>
            <label className={labelClass}>{t('portaalBeheer.vestiging', 'Vestiging (optioneel)')}</label>
            <select
              className={inputClass}
              value={form.location_id}
              onChange={(e) => setForm((p) => ({ ...p, location_id: e.target.value, department_id: '' }))}
            >
              <option value="">{t('portaalBeheer.alleVestigingen', 'Alle vestigingen')}</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>{loc.naam}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>{t('portaalBeheer.afdeling', 'Afdeling (optioneel)')}</label>
            <select
              className={inputClass}
              value={form.department_id}
              onChange={(e) => setForm((p) => ({ ...p, department_id: e.target.value }))}
            >
              <option value="">{t('portaalBeheer.alleAfdelingen', 'Alle afdelingen')}</option>
              {afdelingOpties.map((dep) => (
                <option key={dep.id} value={dep.id}>
                  {dep.naam}{dep.location_naam ? ` (${dep.location_naam})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>{t('portaalBeheer.maxGebruik', 'Max. aantal registraties (optioneel)')}</label>
            <input
              type="number"
              min={1}
              className={inputClass}
              placeholder={t('portaalBeheer.onbeperkt', 'onbeperkt')}
              value={form.max_gebruik}
              onChange={(e) => setForm((p) => ({ ...p, max_gebruik: e.target.value }))}
            />
          </div>
          <div>
            <label className={labelClass}>{t('portaalBeheer.verlooptOp', 'Verloopt op (optioneel)')}</label>
            <input
              type="date"
              className={inputClass}
              value={form.verloopt_op}
              onChange={(e) => setForm((p) => ({ ...p, verloopt_op: e.target.value }))}
            />
          </div>
          <div className="flex items-end">
            <Button type="submit" loading={maakMutation.isPending}>
              <Plus className="w-4 h-4 mr-1" />
              {t('portaalBeheer.codeAanmaken', 'Toegangscode aanmaken')}
            </Button>
          </div>
        </form>
      </Card>

      {/* Bestaande codes */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <KeyRound className="w-5 h-5 text-blue-600" />
          {t('portaalBeheer.codesTitel', 'Toegangscodes')}
        </h2>
        {toegangscodes.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">
            {t('portaalBeheer.geenCodes', 'Nog geen toegangscodes — maak hierboven uw eerste code aan.')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  {[
                    t('portaalBeheer.colCode', 'Code'),
                    t('portaalBeheer.colOmschrijving', 'Omschrijving'),
                    t('portaalBeheer.colScope', 'Vestiging / afdeling'),
                    t('portaalBeheer.colGebruik', 'Gebruikt'),
                    t('portaalBeheer.colVerloopt', 'Verloopt op'),
                    t('portaalBeheer.colStatus', 'Status'),
                  ].map((h) => (
                    <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
                  ))}
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {toegangscodes.map((c) => (
                  <tr key={c.id} className={c.is_active ? '' : 'opacity-60'}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        <code className="font-mono text-sm font-semibold tracking-wider text-gray-900">{c.code}</code>
                        <CopyButton value={c.code} label={t('portaalBeheer.kopieerCode', 'Code kopiëren')} />
                        <CopyButton value={registratieLink(c.code)} label={t('portaalBeheer.kopieerLink', 'Link kopiëren')} className="hidden sm:inline-flex" />
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800">{c.omschrijving || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {[c.vestiging_naam, c.afdeling_naam].filter(Boolean).join(' / ') || t('portaalBeheer.heleOrganisatie', 'Hele organisatie')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800 tabular-nums whitespace-nowrap">
                      {c.gebruikt} / {c.max_gebruik ?? '∞'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{fmtDate(c.verloopt_op)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {c.is_active ? (
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-800">
                          {t('portaalBeheer.actief', 'Actief')}
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                          {t('portaalBeheer.inactief', 'Gedeactiveerd')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {c.is_active && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={deactiveerMutation.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                t(
                                  'portaalBeheer.deactiveerConfirm',
                                  'Toegangscode {{code}} deactiveren? Nieuwe registraties met deze code zijn daarna niet meer mogelijk; bestaande medewerkers behouden toegang.',
                                  { code: c.code }
                                )
                              )
                            ) {
                              deactiveerMutation.mutate(c.id);
                            }
                          }}
                        >
                          <Ban className="w-4 h-4 mr-1" />
                          {t('portaalBeheer.deactiveren', 'Deactiveren')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

// ─── Tab 2: Academy ───────────────────────────────────────────────────────────

export const AcademyTab: React.FC<{ data: EmployerPortaalOverzicht }> = ({ data }) => {
  const { t } = useTranslation('employer');
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Omit<AcademySettings, 'employer_id'>>(() => {
    const { employer_id: _ignored, ...rest } = data.academy;
    return rest;
  });
  const [budgetInput, setBudgetInput] = useState<string>(String(data.academy.budget_per_medewerker ?? ''));
  const [caoPercentageInput, setCaoPercentageInput] = useState<string>(String(data.academy.cao_percentage ?? ''));
  const [hobbyCapActief, setHobbyCapActief] = useState<boolean>(data.academy.hobby_cap_bedrag !== null);
  const [hobbyCapInput, setHobbyCapInput] = useState<string>(String(data.academy.hobby_cap_bedrag ?? '300'));
  const [caoBestand, setCaoBestand] = useState<File | null>(null);
  const [caoResultaat, setCaoResultaat] = useState<CaoImportResultaat | null>(null);

  // Server-waarden opnieuw inladen na een refetch (bijv. na opslaan)
  useEffect(() => {
    const { employer_id: _ignored, ...rest } = data.academy;
    setForm(rest);
    setBudgetInput(String(data.academy.budget_per_medewerker ?? ''));
    setCaoPercentageInput(String(data.academy.cao_percentage ?? ''));
    setHobbyCapActief(data.academy.hobby_cap_bedrag !== null);
    setHobbyCapInput(String(data.academy.hobby_cap_bedrag ?? '300'));
  }, [data.academy]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateAcademySettings({
        ...form,
        budget_per_medewerker: form.model === 'budget' ? Number(budgetInput || 0) : form.budget_per_medewerker,
        cao_percentage: form.model === 'cao_percentage' ? Number(caoPercentageInput || 0) : form.cao_percentage,
        hobby_cap_bedrag: hobbyCapActief ? Number(hobbyCapInput || 0) : null,
      }),
    onSuccess: () => {
      toast.success(t('portaalBeheer.academyOpgeslagen', 'Academy-instellingen opgeslagen'));
      queryClient.invalidateQueries({ queryKey: PORTAAL_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['employer-wkr'] });
    },
    onError: (err: any) => toast.error(apiMessage(err, t('portaalBeheer.academyFout', 'Opslaan mislukt'))),
  });

  const caoImportMutation = useMutation({
    mutationFn: () => {
      if (!caoBestand) throw new Error('Kies eerst een CSV-bestand.');
      return uploadCaoBudgetten(caoBestand);
    },
    onSuccess: (resultaat) => {
      setCaoResultaat(resultaat);
      toast.success(t('portaalBeheer.caoImportOk', { count: resultaat.geimporteerd, defaultValue: '{{count}} medewerkers geïmporteerd.' }));
    },
    onError: (err: any) => toast.error(apiMessage(err, t('portaalBeheer.caoImportFout', 'Importeren mislukt'))),
  });

  // WKR-overzicht per boekjaar
  const huidigJaar = new Date().getFullYear();
  const jaren = [huidigJaar, huidigJaar - 1, huidigJaar - 2];
  const [jaar, setJaar] = useState<number>(data.wkr.boekjaar || huidigJaar);
  const {
    data: wkr,
    isFetching: wkrLaden,
    isError: wkrFout,
  } = useQuery<WkrOverzicht>({
    queryKey: ['employer-wkr', jaar],
    queryFn: () => getWkrOverzicht(jaar),
    initialData: jaar === data.wkr.boekjaar ? data.wkr : undefined,
    placeholderData: (prev) => prev,
  });

  return (
    <div className="space-y-6">
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <GraduationCap className="w-5 h-5 text-blue-600" />
          {t('portaalBeheer.academyTitel', 'BeLagom Academy')}
        </h2>
        <p className="text-sm text-gray-600 mb-5">
          {t(
            'portaalBeheer.academyUitleg',
            'Medewerkers boeken via het portaal zelf trainingen uit de Academy. U bepaalt het model: een vast jaarbudget per medewerker of een on-demand budgetpoort. Medewerkers krijgen automatisch 5% inkoopkorting.'
          )}
        </p>

        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            saveMutation.mutate();
          }}
        >
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
            />
            <span className="text-sm font-medium text-gray-900">
              {t('portaalBeheer.academyActief', 'Academy actief voor medewerkers')}
            </span>
          </label>

          <fieldset className={form.is_active ? '' : 'opacity-50'}>
            <legend className="text-sm font-medium text-gray-700 mb-2">{t('portaalBeheer.model', 'Model')}</legend>
            <div className="space-y-2">
              <label
                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
                  form.model === 'budget' ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <input
                  type="radio"
                  name="academy-model"
                  className="mt-1 text-blue-600 focus:ring-blue-500"
                  checked={form.model === 'budget'}
                  onChange={() => setForm((p) => ({ ...p, model: 'budget' }))}
                />
                <span className="flex-1">
                  <span className="block text-sm font-medium text-gray-900">
                    {t('portaalBeheer.modelBudget', 'Vast L&D-budget per medewerker per jaar')}
                  </span>
                  {form.model === 'budget' && (
                    <span className="mt-2 flex items-center gap-2">
                      <span className="text-sm text-gray-600">€</span>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        className={`${inputClass} w-40`}
                        value={budgetInput}
                        onChange={(e) => setBudgetInput(e.target.value)}
                        aria-label={t('portaalBeheer.budgetPerMedewerker', 'Budget per medewerker per jaar')}
                      />
                      <span className="text-xs text-gray-500">{t('portaalBeheer.perMedewerkerJaar', 'per medewerker per jaar')}</span>
                    </span>
                  )}
                </span>
              </label>
              <label
                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
                  form.model === 'on_demand' ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <input
                  type="radio"
                  name="academy-model"
                  className="mt-1 text-blue-600 focus:ring-blue-500"
                  checked={form.model === 'on_demand'}
                  onChange={() => setForm((p) => ({ ...p, model: 'on_demand' }))}
                />
                <span className="block text-sm font-medium text-gray-900">
                  {t('portaalBeheer.modelOnDemand', 'On-demand: onbeperkte virtuele budgetpoort')}
                </span>
              </label>
              <label
                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
                  form.model === 'payroll_bridge' ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <input
                  type="radio"
                  name="academy-model"
                  className="mt-1 text-blue-600 focus:ring-blue-500"
                  checked={form.model === 'payroll_bridge'}
                  onChange={() => setForm((p) => ({ ...p, model: 'payroll_bridge' }))}
                />
                <span className="flex-1">
                  <span className="block text-sm font-medium text-gray-900">
                    {t('portaalBeheer.modelPayroll', 'Payroll Bridge: twee virtuele potten per medewerker (professioneel + hobby) uit uw HR-export')}
                  </span>
                  <span className="block text-xs text-gray-600 mt-1">
                    {t('portaalBeheer.modelPayrollUitleg', 'Wordt automatisch actief na een import via de Payroll Bridge hierboven. Pot 1 = gerichte vrijstelling, pot 2 = WKR vrije ruimte; saldo lekt nooit tussen de potten.')}
                  </span>
                </span>
              </label>
              <label
                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
                  form.model === 'cao_percentage' ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <input
                  type="radio"
                  name="academy-model"
                  className="mt-1 text-blue-600 focus:ring-blue-500"
                  checked={form.model === 'cao_percentage'}
                  onChange={() => setForm((p) => ({ ...p, model: 'cao_percentage' }))}
                />
                <span className="flex-1">
                  <span className="block text-sm font-medium text-gray-900">
                    {t('portaalBeheer.modelCao', 'CAO-percentage van het brutosalaris (IKB-regeling)')}
                  </span>
                  {form.model === 'cao_percentage' && (
                    <div className="mt-3 space-y-3">
                      <span className="flex items-center gap-2">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          className={`${inputClass} w-28`}
                          value={caoPercentageInput}
                          onChange={(e) => setCaoPercentageInput(e.target.value)}
                          aria-label={t('portaalBeheer.caoPercentage', 'CAO-percentage')}
                        />
                        <span className="text-sm text-gray-600">% {t('portaalBeheer.vanBrutoJaarsalaris', 'van het bruto jaarsalaris')}</span>
                      </span>

                      <div className="rounded-lg border border-dashed border-gray-300 p-3 bg-white">
                        <p className="text-xs text-gray-600 mb-2">
                          {t(
                            'portaalBeheer.caoUploadUitleg',
                            'Upload de geanonimiseerde IKB-staffel: twee kolommen, SHA-256 telefoon-hash en bruto jaarsalaris. Nooit namen, e-mailadressen of BSN-nummers.'
                          )}
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="file"
                            accept=".csv,text/csv"
                            onChange={(e) => setCaoBestand(e.target.files?.[0] ?? null)}
                            className="text-sm"
                          />
                          <Button
                            type="button"
                            size="sm"
                            loading={caoImportMutation.isPending}
                            disabled={!caoBestand}
                            onClick={() => caoImportMutation.mutate()}
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            {t('portaalBeheer.caoUploadKnop', 'Upload IKB-staffel (CSV)')}
                          </Button>
                        </div>
                        {caoResultaat && (
                          <p className="text-xs text-gray-600 mt-2">
                            {t('portaalBeheer.caoImportSamenvatting', `${caoResultaat.geimporteerd} rijen geïmporteerd, ${caoResultaat.overgeslagen} overgeslagen.`)}
                            {caoResultaat.fouten.length > 0 && (
                              <span className="block text-amber-700 mt-1">{caoResultaat.fouten.slice(0, 5).join(' ')}</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </span>
              </label>
            </div>
          </fieldset>

          <fieldset className={form.is_active ? '' : 'opacity-50'}>
            <legend className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              {t('portaalBeheer.wkrBeheersing', 'WKR-Beheersing')}
            </legend>
            <div className="space-y-2">
              <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                <p className="text-sm font-medium text-gray-900">
                  {t('portaalBeheer.wkrProfessioneel', 'Professionele Groei & Loopbaan — altijd toegestaan (onbeperkt)')}
                </p>
                <p className="text-xs text-green-800 mt-1">
                  {t(
                    'portaalBeheer.wkrProfessioneelUitleg',
                    'Valt onder de gerichte vrijstelling van de Belastingdienst. Deze uitgaven zijn 100% belastingvrij en drukken voor € 0,- op uw WKR-vrije ruimte.'
                  )}
                </p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <label className="flex items-center gap-3 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    checked={hobbyCapActief}
                    onChange={(e) => setHobbyCapActief(e.target.checked)}
                  />
                  <span className="text-sm font-medium text-gray-900">
                    {t('portaalBeheer.wkrHobbyToggle', 'Passie, Hobby & Lifestyle — toegestaan, maar begrensd via een harde software-cap')}
                  </span>
                </label>
                {hobbyCapActief && (
                  <span className="flex items-center gap-2 ml-7">
                    <span className="text-sm text-gray-600">€</span>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      className={`${inputClass} w-32`}
                      value={hobbyCapInput}
                      onChange={(e) => setHobbyCapInput(e.target.value)}
                      aria-label={t('portaalBeheer.wkrHobbyCap', 'Maximaal hobbybudget per medewerker per jaar')}
                    />
                    <span className="text-xs text-gray-500">{t('portaalBeheer.perMedewerkerJaar', 'per medewerker per jaar')}</span>
                  </span>
                )}
                <p className="text-xs text-amber-800 mt-2 ml-7">
                  {t(
                    'portaalBeheer.wkrHobbyUitleg',
                    'Hobby-uitgaven worden door de fiscus gezien als loon in natura en drukken op uw WKR-vrije ruimte. De software blokkeert dit aanbod automatisch per medewerker zodra deze grens is bereikt.'
                  )}
                </p>
              </div>
            </div>
          </fieldset>

          <div className="max-w-xs">
            <label className={labelClass}>{t('portaalBeheer.thermometerFrequentie', 'Frequentie Bedrijfsthermometer')}</label>
            <select
              className={inputClass}
              value={form.thermometer_frequentie}
              onChange={(e) => setForm((p) => ({ ...p, thermometer_frequentie: e.target.value as 'kwartaal' | 'jaar' }))}
            >
              <option value="kwartaal">{t('portaalBeheer.freqKwartaal', 'Elk kwartaal')}</option>
              <option value="jaar">{t('portaalBeheer.freqJaar', 'Eén keer per jaar')}</option>
            </select>
          </div>

          <Button type="submit" loading={saveMutation.isPending}>
            {t('portaalBeheer.academyOpslaan', 'Instellingen opslaan')}
          </Button>
        </form>
      </Card>

      <Card>
        <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('portaalBeheer.wkrTitel', 'WKR-overzicht')}
          </h2>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-700">{t('portaalBeheer.boekjaar', 'Boekjaar')}</label>
            <select className={`${inputClass} w-auto`} value={jaar} onChange={(e) => setJaar(Number(e.target.value))}>
              {jaren.map((j) => (
                <option key={j} value={j}>{j}</option>
              ))}
            </select>
            {wkrLaden && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          {t('portaalBeheer.wkrUitleg', 'Alleen totalen — nooit per medewerker.')}
        </p>

        {wkrFout && (
          <p className="text-sm text-red-600">{t('portaalBeheer.wkrFout', 'WKR-overzicht kon niet worden geladen.')}</p>
        )}

        {wkr && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="text-2xl font-bold text-gray-900">{wkr.aantal_medewerkers}</div>
                <div className="text-xs text-gray-500 mt-1">{t('portaalBeheer.wkrMedewerkers', 'medewerkers met Academy-wallet')}</div>
              </div>
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="text-2xl font-bold text-gray-900">
                  {wkr.budget_gereserveerd === null ? '—' : fmtEuro(wkr.budget_gereserveerd)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {wkr.budget_gereserveerd === null
                    ? t('portaalBeheer.wkrOnDemand', 'on-demand: geen vast budget gereserveerd')
                    : t('portaalBeheer.wkrGereserveerd', 'budget gereserveerd dit boekjaar')}
                </div>
              </div>
            </div>

            <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('portaalBeheer.wkrPerLabel', 'Per fiscale behandeling')}</h3>
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('portaalBeheer.colWkrLabel', 'Behandeling')}</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('portaalBeheer.colAantal', 'Aantal')}</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('portaalBeheer.colWallet', 'Uit wallet')}</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('portaalBeheer.colKorting', 'Korting (5% inkoopkorting)')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {wkr.per_wkr_label.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-4 text-sm text-gray-500 text-center">
                        {t('portaalBeheer.wkrGeenBoekingen', 'Nog geen boekingen in dit boekjaar.')}
                      </td>
                    </tr>
                  ) : (
                    wkr.per_wkr_label.map((r) => (
                      <tr key={r.wkr_label}>
                        <td className="px-4 py-3 text-sm text-gray-900">{WKR_LABELS[r.wkr_label] || r.wkr_label}</td>
                        <td className="px-4 py-3 text-sm text-right tabular-nums">{r.aantal}</td>
                        <td className="px-4 py-3 text-sm text-right tabular-nums">{fmtEuro(r.wallet_totaal)}</td>
                        <td className="px-4 py-3 text-sm text-right tabular-nums">{fmtEuro(r.korting_totaal)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('portaalBeheer.wkrPerCategorie', 'Per Academy-categorie')}</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('portaalBeheer.colCategorie', 'Categorie')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('portaalBeheer.colWkrLabel', 'Behandeling')}</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('portaalBeheer.colAantal', 'Aantal')}</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('portaalBeheer.colWallet', 'Uit wallet')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {wkr.per_categorie.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-4 text-sm text-gray-500 text-center">
                        {t('portaalBeheer.wkrGeenBoekingen', 'Nog geen boekingen in dit boekjaar.')}
                      </td>
                    </tr>
                  ) : (
                    wkr.per_categorie.map((r) => {
                      const wkrLabel = data.categorieen[r.categorie]?.wkr_label;
                      return (
                        <tr key={r.categorie}>
                          <td className="px-4 py-3 text-sm text-gray-900">{r.label}</td>
                          <td className="px-4 py-3 text-xs text-gray-600">{wkrLabel ? WKR_LABELS[wkrLabel] : '—'}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums">{r.aantal}</td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums">{fmtEuro(r.wallet_totaal)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

// ─── Tab 3: Thermometer (hittekaart) ──────────────────────────────────────────

const SOORT_LABEL: Record<HittekaartGroep['soort'], string> = {
  organisatie: 'Organisatie',
  vestiging: 'Vestiging',
  afdeling: 'Afdeling',
};
const SOORT_VOLGORDE: Record<HittekaartGroep['soort'], number> = { organisatie: 0, vestiging: 1, afdeling: 2 };

const scoreKleur = (score: number): string => {
  if (score <= 2) return 'bg-red-200 text-red-900';
  if (score < 3) return 'bg-amber-200 text-amber-900';
  if (score < 4) return 'bg-lime-200 text-lime-900';
  return 'bg-green-300 text-green-900';
};

const LEGENDA: Array<{ klasse: string; label: string }> = [
  { klasse: 'bg-red-200', label: '≤ 2,0 — zorgelijk' },
  { klasse: 'bg-amber-200', label: '2,0 – 3,0 — aandacht' },
  { klasse: 'bg-lime-200', label: '3,0 – 4,0 — redelijk' },
  { klasse: 'bg-green-300', label: '≥ 4,0 — goed' },
  { klasse: 'bg-gray-200', label: 'afgeschermd (privacy)' },
];

const ThermometerTab: React.FC = () => {
  const { t } = useTranslation('employer');
  const [rondeId, setRondeId] = useState<string | null>(null);

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['employer-hittekaart', rondeId],
    queryFn: () => getHittekaart(rondeId),
    placeholderData: (prev) => prev,
  });

  const groepen = useMemo(
    () => (data ? [...data.groepen].sort((a, b) => SOORT_VOLGORDE[a.soort] - SOORT_VOLGORDE[b.soort]) : []),
    [data]
  );

  if (isLoading) return <Spinner />;

  if (isError || !data) {
    return (
      <Card>
        <p className="text-sm text-red-600 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {apiMessage(error, t('portaalBeheer.hittekaartFout', 'De hittekaart kon niet worden geladen.'))}
        </p>
      </Card>
    );
  }

  const geenRondes = data.rondes.length === 0;

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Thermometer className="w-5 h-5 text-blue-600" />
            {t('portaalBeheer.hittekaartTitel', 'Hittekaart Bedrijfsthermometer')}
          </h2>
          {!geenRondes && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-700">{t('portaalBeheer.ronde', 'Ronde')}</label>
              <select
                className={`${inputClass} w-auto`}
                value={rondeId ?? data.ronde.id}
                onChange={(e) => setRondeId(e.target.value)}
              >
                {data.rondes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.periode_label}
                    {r.loopt ? ` (${t('portaalBeheer.rondeLooptKort', 'loopt nog')})` : ''}
                  </option>
                ))}
              </select>
              {isFetching && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
            </div>
          )}
        </div>
        <p className="text-sm text-gray-600 mb-4">
          {t(
            'portaalBeheer.hittekaartUitleg',
            'Medewerkers beantwoorden anoniem vier pijlers op een schaal van 1 (laag) tot 5 (hoog). U ziet gemiddelden per groep, nooit individuele antwoorden.'
          )}
        </p>

        {geenRondes ? (
          <p className="text-sm text-gray-500 py-6 text-center">
            {t('portaalBeheer.geenRondes', 'Er is nog geen ronde van de Bedrijfsthermometer gestart.')}
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm mb-4">
              <span className="text-gray-800">
                <strong className="text-blue-700">{data.aantal_ingevuld}</strong>{' '}
                {t('portaalBeheer.hittekaartAantal', 'van {{totaal}} medewerkers hebben deze ronde ingevuld', { totaal: data.aantal_medewerkers })}
              </span>
              <span className="text-gray-600 flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                {t(
                  'portaalBeheer.minimumGroep',
                  'Groepen met minder dan {{minimum_groep}} antwoorden, of die uit de andere groepen terug te rekenen zijn, worden afgeschermd',
                  { minimum_groep: data.minimum_groep }
                )}
              </span>
            </div>

            {data.ronde_loopt ? (
              <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-4 py-3">
                {t(
                  'portaalBeheer.rondeLoopt',
                  'Deze ronde loopt nog tot en met {{eind}}. De resultaten verschijnen na afloop: tijdens de ronde zou elke nieuwe inzending het gemiddelde zichtbaar verschuiven en daarmee een individueel antwoord verraden.',
                  { eind: new Date(data.ronde.eind_op).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) }
                )}
              </p>
            ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border-separate border-spacing-1">
                <thead>
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('portaalBeheer.colGroep', 'Groep')}</th>
                    <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">n</th>
                    {data.pijlers.map((p) => (
                      <th key={p.key} className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap" title={p.vraag}>
                        {p.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groepen.map((g) => (
                    <tr key={`${g.soort}-${g.id ?? 'org'}`}>
                      <td className="px-2 py-1.5 text-sm whitespace-nowrap">
                        <span className={g.soort === 'organisatie' ? 'font-semibold text-gray-900' : 'text-gray-800'}>{g.naam}</span>
                        {g.soort !== 'organisatie' && (
                          <span className="ml-2 text-xs text-gray-400">{SOORT_LABEL[g.soort]}</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-sm text-right text-gray-600 tabular-nums">{g.aantal ?? '–'}</td>
                      {data.pijlers.map((p) => {
                        const score = g.onvoldoende_respons || !g.scores ? null : g.scores[p.key as ThermometerPijlerKey];
                        return (
                          <td key={p.key} className="p-0">
                            {score === null || score === undefined ? (
                              <div
                                className="rounded-md bg-gray-200 text-gray-500 text-center text-xs py-2 px-3 min-w-[4.5rem]"
                                title={t('portaalBeheer.onvoldoendeRespons', 'Afgeschermd: minder dan {{minimum_groep}} antwoorden, of terug te rekenen uit de andere groepen', { minimum_groep: data.minimum_groep })}
                              >
                                <div className="font-medium">{t('portaalBeheer.afgeschermd', 'afgeschermd')}</div>
                              </div>
                            ) : (
                              <div className={`rounded-md text-center text-sm font-semibold py-2 px-3 min-w-[4.5rem] tabular-nums ${scoreKleur(score)}`}>
                                {score.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}

            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-600">
              {LEGENDA.map((l) => (
                <span key={l.label} className="inline-flex items-center gap-1.5">
                  <span className={`inline-block w-4 h-4 rounded ${l.klasse}`} />
                  {l.label}
                </span>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

// ─── Tab 4: Activeren (bulk-activatieketen) ───────────────────────────────────

const ActiverenTab: React.FC<{ data: EmployerPortaalOverzicht }> = ({ data }) => {
  const { t } = useTranslation('employer');
  const queryClient = useQueryClient();
  const [bestand, setBestand] = useState<File | null>(null);
  const [importResultaat, setImportResultaat] = useState<UitnodigingenImportResultaat | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: PORTAAL_QUERY_KEY });

  const importMutation = useMutation({
    mutationFn: (file: File) => importUitnodigingen(file),
    onSuccess: (resultaat) => {
      setImportResultaat(resultaat);
      toast.success(t('portaalBeheer.importGelukt', '{{n}} rij(en) geïmporteerd', { n: resultaat.geimporteerd }));
      setBestand(null);
      invalidate();
    },
    onError: (err: any) => toast.error(apiMessage(err, t('portaalBeheer.importFout', 'Importeren mislukt'))),
  });

  const lanceerMutation = useMutation({
    mutationFn: () => activeerUitnodigingen(),
    onSuccess: (resultaat) => {
      toast.success(
        t('portaalBeheer.lanceerGelukt', '{{n}} uitnodiging(en) verstuurd', { n: resultaat.verstuurd })
      );
      invalidate();
    },
    onError: (err: any) => toast.error(apiMessage(err, t('portaalBeheer.lanceerFout', 'Versturen mislukt'))),
  });

  const { uitnodigingen: status, pool_contract_actief: poolActief } = data;
  const kanLanceren = poolActief && status.totaal - status.verzonden - status.mislukt > 0;

  return (
    <div className="space-y-6">
      {!poolActief && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-2 text-sm text-amber-900">
          <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <span>
            {t(
              'portaalBeheer.geenPoolContract',
              'De launch-knop wordt actief zodra er een getekend Model-OvO is voor uw bedrijfspool.'
            )}
          </span>
        </div>
      )}

      <Card>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <Upload className="w-5 h-5 text-blue-600" />
          {t('portaalBeheer.importTitel', 'Personeelslijst importeren')}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          {t(
            'portaalBeheer.importUitleg',
            'CSV met kolommen voornaam, telefoon, email. Deze gegevens worden alleen gebruikt om de uitnodiging te versturen — nooit teruggegeven aan uw cockpit, en na activatie automatisch geanonimiseerd.'
          )}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 items-start">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setBestand(e.target.files?.[0] ?? null)}
            className="text-sm text-gray-700"
          />
          <Button
            type="button"
            disabled={!bestand || importMutation.isPending}
            loading={importMutation.isPending}
            onClick={() => bestand && importMutation.mutate(bestand)}
          >
            <Upload className="w-4 h-4 mr-1" />
            {t('portaalBeheer.importeren', 'Importeren')}
          </Button>
        </div>
        {importResultaat && (
          <div className="mt-4 rounded-lg border border-gray-200 p-3 text-sm">
            <p className="text-gray-900">
              {t('portaalBeheer.importResultaat', '{{geimporteerd}} geïmporteerd, {{overgeslagen}} overgeslagen', {
                geimporteerd: importResultaat.geimporteerd,
                overgeslagen: importResultaat.overgeslagen,
              })}
            </p>
            {importResultaat.fouten.length > 0 && (
              <ul className="mt-2 text-red-600 list-disc list-inside">
                {importResultaat.fouten.slice(0, 10).map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>

      <Card>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <Rocket className="w-5 h-5 text-blue-600" />
          {t('portaalBeheer.lanceerTitel', 'Lanceer & Activeer Medewerkerportaal')}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          {t(
            'portaalBeheer.lanceerUitleg',
            'Verstuurt een gepersonaliseerde SMS en e-mail naar iedereen die nog niet is uitgenodigd.'
          )}
        </p>

        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-700">
              {t('portaalBeheer.activatiegraad', 'Activatie-graad')}
            </span>
            <span className="font-semibold text-gray-900 tabular-nums">
              {status.percentage}% ({status.geactiveerd} {t('portaalBeheer.van', 'van')} {status.totaal})
            </span>
          </div>
          <div className="h-2.5 w-full rounded-full bg-gray-200 overflow-hidden">
            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${status.percentage}%` }} />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {t('portaalBeheer.statusSamenvatting', '{{verzonden}} verstuurd, {{mislukt}} mislukt', {
              verzonden: status.verzonden,
              mislukt: status.mislukt,
            })}
          </p>
        </div>

        <Button
          type="button"
          size="lg"
          disabled={!kanLanceren || lanceerMutation.isPending}
          loading={lanceerMutation.isPending}
          onClick={() => lanceerMutation.mutate()}
        >
          <Send className="w-4 h-4 mr-1" />
          {t('portaalBeheer.lanceerKnop', 'Lanceer & Activeer Medewerkerportaal')}
        </Button>
        {poolActief && !kanLanceren && (
          <p className="text-xs text-gray-500 mt-2">
            {t('portaalBeheer.geenOpenstaand', 'Geen openstaande uitnodigingen — importeer eerst een personeelslijst.')}
          </p>
        )}
      </Card>
    </div>
  );
};

// ─── Pagina ───────────────────────────────────────────────────────────────────

export const EmployerMedewerkersportaalPage: React.FC = () => {
  const { t } = useTranslation('employer');
  const [searchParams, setSearchParams] = useSearchParams();

  useCockpitTitle(
    t('portaalBeheer.title', 'Medewerkersportaal'),
    t('portaalBeheer.subtitle', 'Anonieme toegang voor uw medewerkers: professionals, Academy-budget en Bedrijfsthermometer')
  );

  const paramTab = searchParams.get('tab') as PortaalTab | null;
  const activeTab: PortaalTab = paramTab && TABS.some((tab) => tab.key === paramTab) ? paramTab : 'toegang';
  const selectTab = (tab: PortaalTab) => setSearchParams({ tab }, { replace: true });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: PORTAAL_QUERY_KEY,
    queryFn: getEmployerPortaal,
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-col lg:flex-row gap-6">
        <nav className="lg:w-72 flex-shrink-0" aria-label={t('portaalBeheer.navLabel', 'Medewerkersportaal-onderdelen')}>
          <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => selectTab(tab.key)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`flex items-start gap-3 px-4 py-3 rounded-lg border text-left transition-colors flex-shrink-0 lg:flex-shrink ${
                    isActive ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span>
                    <span className="block text-sm font-medium">{t(`portaalBeheer.tab_${tab.key}`, tab.label)}</span>
                    <span className="hidden lg:block text-xs text-gray-500 mt-0.5">{t(`portaalBeheer.tabDesc_${tab.key}`, tab.description)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        <div className="flex-1 min-w-0">
          {isLoading ? (
            <Spinner />
          ) : isError || !data ? (
            <Card>
              <p className="text-sm text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {apiMessage(error, t('portaalBeheer.laadFout', 'Het medewerkersportaal kon niet worden geladen.'))}
              </p>
            </Card>
          ) : (
            <>
              {activeTab === 'toegang' && <ToegangTab data={data} />}
              {activeTab === 'academy' && <AcademyTab data={data} />}
              {activeTab === 'activeren' && <ActiverenTab data={data} />}
              {activeTab === 'thermometer' && <ThermometerTab />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
